import { inject, Injectable, isDevMode } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { ConfirmationAttachment } from "@bitwarden/web-vault/app/models/types/general-types";
import { ConfirmationDialogComponent } from "../../components/confirmation-dialog/confirmation-dialog.component";
import { mobileBottomSheetConfig } from "../../components/dialog/mobile-bottom-sheet-dialog";
import { confirmationMapper } from "./confirmation.constant";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { NotificationType } from "@bitwarden/web-vault/app/models/types/confirmation.types";
import { CtaNotificationComponent } from "@bitwarden/web-vault/app/components/cta-notification/cta-notification.component";

export interface CtaNotificationData {
  title: string;
  message: string;
  cancel: { method: () => void; title: string };
  confirm: { method: () => void; title: string };
  reminder: string;
}

@Injectable({
  providedIn: "root",
})
export class ConfirmationDialogService {
  private platformUtilsService = inject(PlatformUtilsService);
  private i18nService = inject(I18nService);
  private dialog = inject(MatDialog);

  /**
   * Opens a confirmation dialog based on the provided key.
   *
   * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating the user's confirmation.
   * @param confirmationKey - The key of the confirmation dialog to show.
   * @param attachment - An optional attachment to pass to the confirmation dialog.
   */
  async confirmFor(
    confirmationKey: ConfirmationEnum,
    attachment?: ConfirmationAttachment,
  ): Promise<boolean> {
    return (await this.choiceFor(confirmationKey, attachment)) ?? false;
  }

  /**
   * Like {@link confirmFor}, but keeps a dismissal (close X, Esc, backdrop — see
   * {@link ConfirmationType.dismissible}) distinct from the "no" button.
   *
   * @returns true for "yes", false for "no", null when dismissed.
   */
  async choiceFor(
    confirmationKey: ConfirmationEnum,
    attachment?: ConfirmationAttachment,
  ): Promise<boolean | null> {
    const confirmationData = confirmationMapper[confirmationKey];
    const {
      title,
      message,
      type,
      singleButton,
      customizedButton,
      stayOpenForPostConfirmAction,
      dismissible,
      destructive,
    } = confirmationData;

    // A "$GENERATED$" title arrives already translated from the call site; flag it so the
    // dialog skips i18n for it instead of guessing from the string's shape.
    const isTitleGenerated = title === "$GENERATED$";
    if (isDevMode() && isTitleGenerated && !attachment?.titleReplacement) {
      // eslint-disable-next-line no-console
      console.warn(
        `[ConfirmationDialog] "${confirmationKey}" has a $GENERATED$ title but no titleReplacement`,
      );
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      ...mobileBottomSheetConfig(),
      disableClose: !dismissible,
      data: {
        title: isTitleGenerated ? (attachment?.titleReplacement ?? "") : title || "info",
        isTitleGenerated,
        type,
        message: attachment && message === "$GENERATED$" ? attachment.messageReplacement : message,
        templateContent: attachment?.templateContent,
        singleButton: singleButton ?? false,
        customizedButton,
        stayOpenForPostConfirmAction: stayOpenForPostConfirmAction ?? false,
        stayOpenAfterConfirmAction: attachment?.stayOpenAfterConfirmAction,
        dismissible: dismissible ?? false,
        destructive: destructive ?? false,
      },
    });

    // A bare `mat-dialog-close` (e.g. the close X) closes with "" — treat anything
    // but an explicit yes/no as a dismissal.
    const result = await firstValueFrom(dialogRef.afterClosed());
    return typeof result === "boolean" ? result : null;
  }

  async ctaNotification(
    data: CtaNotificationData,
  ): Promise<MatDialogRef<CtaNotificationComponent>> {
    return this.dialog.open(CtaNotificationComponent, {
      panelClass: "cat-notification-dialog",
      disableClose: true,
      width: "600px",
      maxWidth: "95vw",
      data,
    });
  }

  notify(
    type: NotificationType,
    titleKey: string,
    textKey: string,
    useI18nService: boolean = true,
  ) {
    this.platformUtilsService.showToast(
      type,
      useI18nService ? this.i18nService.t(titleKey) : titleKey,
      useI18nService ? this.i18nService.t(textKey) : textKey,
    );
  }
}
